<template>
	<div class="add-visit-payment">
		<!-- header -->
		<div class="d-flex align-center px-4 py-3 add-visit-payment__header" @click="back">
			<v-icon class="me-2">{{ backIcon }}</v-icon>
			<span class="text-subtitle-1 font-weight-medium">{{ $T('Visits.add-visit') }}</span>
		</div>

		<v-form ref="form">
			<v-simple-table dense class="add-visit-payment__table">
				<template v-slot:default>
					<thead class="primary white--text">
						<tr>
							<th class="white--text">{{ $T('inputs.payment-type') }}</th>
							<th class="white--text">{{ $T('inputs.paid-amount') }}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="row in payments"
							:key="row.id"
							:class="{ 'add-visit-payment__row--empty': isRowEmpty(row) }"
						>
							<td>
								<v-select
									v-model="row.data.paymentMethodId"
									:items="paymentMethods"
									item-text="name"
									item-value="id"
									dense
									flat
									solo
									background-color="transparent"
									hide-details="auto"
									placeholder="-"
									:rules="rules.requiredAsNumber"
									@change="onPaymentMethodChange(row)"
								/>
							</td>
							<td>
								<v-text-field
									v-model.number="row.data.amount"
									type="number"
									min="0"
									dense
									flat
									solo
									background-color="transparent"
									hide-details="auto"
									hide-spin-buttons
									placeholder="-"
									:disabled="row.data.paymentMethodId !== paymentMethod.debit"
									:rules="row.data.paymentMethodId === paymentMethod.debit ? paymentAmountRules() : []"
								/>
							</td>
							<td class="text-center">
								<v-btn v-if="!isRowEmpty(row)" icon small color="error" @click="clearPayment(row.id)">
									<v-icon size="18">mdi-close</v-icon>
								</v-btn>
							</td>
						</tr>
					</tbody>
				</template>
			</v-simple-table>

			<!-- UI-only for now, not sent to visits/create — no backend field exists -->
			<div v-if="false" class="px-4 mt-4">
				<div class="text-body-2 mb-1">{{ $T('Visits.discount-amount') }}</div>
				<v-text-field
					v-model.number="discountAmount"
					type="number"
					min="0"
					outlined
					dense
					hide-spin-buttons
					hide-details
				/>
			</div>
		</v-form>

		<v-footer app fixed padless class="add-visit-payment__footer">
		<v-card flat width="100%" color="white">
			<v-card-text v-if="usePrices" class="pb-0">
				<div class="d-flex justify-space-between text-body-2 py-2">
					<span>{{ $T('Visits.materials-total') }}</span>
					<span>{{ $options.filters.formatNumber(getMaterialsTotal) }} {{ $T('currency') }}</span>
				</div>
				<v-divider />
				<div class="d-flex justify-space-between text-body-2 py-2">
					<span>{{ $T('Visits.returns-total') }}</span>
					<span class="red--text">{{ $options.filters.formatNumber(getReturnsTotal) }} {{ $T('currency') }}</span>
				</div>
				<template v-if="discountAmount">
					<v-divider />
					<div class="d-flex justify-space-between text-body-2 py-2">
						<span>{{ $T('inputs.discount') }}</span>
						<span>{{ $options.filters.formatNumber(discountAmount) }} {{ $T('currency') }}</span>
					</div>
				</template>
				<v-divider />
				<div class="d-flex justify-space-between text-subtitle-1 font-weight-bold py-2">
					<span>{{ $T('Visits.final-total') }}</span>
					<span class="green--text text--darken-1">{{ $options.filters.formatNumber(finalTotal) }} {{ $T('currency') }}</span>
				</div>
			</v-card-text>
			<v-card-actions class="add-visit-payment__actions">
				<v-btn outlined rounded x-large class="add-visit-payment__secondary-btn" @click="back">
					{{ $T('back') }}
				</v-btn>
				<v-btn
					color="success"
					rounded
					x-large
					class="add-visit-payment__primary-btn"
					:loading="loading"
					:disabled="loading"
					@click="submit"
				>
					{{ $T('Visits.confirm-and-save') }}
				</v-btn>
			</v-card-actions>
			<v-progress-linear value="100" height="3" color="primary" />
		</v-card>
		</v-footer>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import rules from '@/helpers/validation rules'
import { paymentMethod, transactionState } from '@/helpers/enums'
import { usePrices, useVisitOrderRequest } from '@/configs/general.configs'

export default {
	name: 'AddVisitPayment',

	data() {
		return {
			usePrices,
			paymentMethod,
			rules,
			moment,

			loading: false,
			payments: [],
			discountAmount: null
		}
	},

	computed: {
		...mapState('visitDraft', {
			visitId: state => state.id,
			entityId: state => state.entityId,
			visitDate: state => state.visitDate,
			priceTypeId: state => state.priceTypeId,
			durationInMinutes: state => state.durationInMinutes,
			invoiceItems: state => state.invoiceItems,
			returnItems: state => state.returnItems,
			orderItems: state => state.orderItems,
			orderDeliveryDate: state => state.orderDeliveryDate,
			orderDeliveryTime: state => state.orderDeliveryTime,
			orderDeliveryPlace: state => state.orderDeliveryPlace,
			marketing: state => state.marketing
		}),

		...mapGetters('visitDraft', ['getMaterialsTotal', 'getReturnsTotal']),

		...mapState({
			userData: state => state.auth.userData,
			serverTime: state => state.configurations.serverTime
		}),

		paymentMethods() {
			return [
				{ id: paymentMethod.cash, name: this.$T('payment-method.cash') },
				{ id: paymentMethod.credit, name: this.$T('payment-method.credit') },
				{ id: paymentMethod.debit, name: this.$T('payment-method.debit') }
			]
		},

		backIcon() {
			return this.$vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left'
		},

		finalTotal() {
			return this.getMaterialsTotal - this.getReturnsTotal - (Number(this.discountAmount) || 0)
		}
	},

	methods: {
		paymentAmountRules() {
			return [val => (val <= this.finalTotal && val > 0) || this.$T('messages.payment-amount-exceeds-remaining')]
		},

		onPaymentMethodChange(row) {
			row.data.amount = row.data.paymentMethodId === paymentMethod.debit
				? 0
				: Math.max(this.finalTotal, 0)
		},

		isRowEmpty(row) {
			return !row.data.paymentMethodId && !row.data.amount
		},

		buildEmptyRow(id) {
			return { id, data: { paymentMethodId: paymentMethod.cash, amount: Math.max(this.finalTotal, 0) } }
		},

		ensureSingleRow() {
			this.payments = this.payments.length ? [this.payments[0]] : [this.buildEmptyRow(1)]
		},

		clearPayment(id) {
			const row = this.payments.find(row => row.id === id)
			if (row) {
				row.data.paymentMethodId = null
				row.data.amount = null
			}
		},

		buildGroupedSamplesPayload() {
			return this.invoiceItems.filter(({ data }) => data.materialId && data.amount).map(({ data }) => ({
				materialId: data.materialId,
				amount: data.amount,
				...(usePrices
					? {
						sellPrice: data.price,
						freeSample: false,
						priceDiscount: data.discount || null,
						priceTypeId: this.priceTypeId
					}
					: {})
			})).reduce((res, item) => {
				const index = res.findIndex(c => item.materialId === c.materialId)
				if (index === -1) res.push(item)
				else res[index].amount += item.amount
				return res
			}, [])
		},

		buildReturnItemsPayload() {
			return this.returnItems.filter(({ data }) => data.materialId && data.amount).map(({ data }) => ({
				materialId: data.materialId,
				amount: data.amount,
				returnedTransaction: true,
				state: data.reason != null ? data.reason : transactionState.undefined,
				...(usePrices
					? {
						sellPrice: data.price,
						priceTypeId: this.priceTypeId
					}
					: {})
			})).reduce((res, item) => {
				const index = res.findIndex(c => item.materialId === c.materialId && item.state === c.state)
				if (index === -1) res.push(item)
				else res[index].amount += item.amount
				return res
			}, [])
		},

		buildOrderDeliveryTime() {
			if (!this.orderDeliveryTime) return null

			const deliveryDate = this.orderDeliveryDate || moment().format('YYYY-MM-DD')

			return moment(`${deliveryDate} ${this.orderDeliveryTime}`, 'YYYY-MM-DD HH:mm').toISOString()
		},

		buildOrderPayload(visitId) {
			const orderItemsPayload = this.orderItems
				.map(({ data }) => ({ materialId: data.materialId, amount: data.amount }))
				.filter(item => item.materialId && item.amount)

			return {
				id: null,
				entityId: this.entityId,
				employeeId: this.userData?.id || null,
				visitId: visitId || null,
				orderCost: 0,
				isPosted: true,
				notes: '',
				deliveryTime: this.buildOrderDeliveryTime(),
				deliveryPlace: this.orderDeliveryPlace || '',
				orderDetail: orderItemsPayload.map(item => ({
					orderId: null,
					materialId: item.materialId,
					amount: item.amount,
					deliverDate: this.orderDeliveryDate || null,
					notes: '',
					personDelivered: '',
					isDone: false
				}))
			}
		},

		handleSaveSuccess(messageKey) {
			this.$eventBus.$emit('show-snackbar', this.$t(messageKey))
			this.$store.dispatch('materialsTransactions/fetchAll')
			if (usePrices) this.$store.dispatch('profile/fetchBalance')

			this.$store.dispatch('visitDraft/reset').then(() => {
				this.$router.push({ name: 'visits' })
			})
		},

		submit() {
			if (!this.$refs.form.validate()) return

			this.loading = true

			const [paymentRow] = this.payments.filter(row => !this.isRowEmpty(row))
			const payment = paymentRow ? paymentRow.data.paymentMethodId : null
			const amountReceived = paymentRow ? (Number(paymentRow.data.amount) || 0) : 0

			const visitPayload = {
				entityId: this.entityId,
				coreMessage: this.marketing.coreMessage,
				durationInMinutes: this.durationInMinutes,
				otherSamples: this.marketing.otherSamples,
				notes: this.marketing.visitNotes,
				objections: this.marketing.objection,
				nextVisitNotes: this.marketing.centerNotes,
				purpose: this.marketing.nextCall,
				materialAmounts: [...this.buildGroupedSamplesPayload(), ...this.buildReturnItemsPayload()],
				unavailableMaterialIds: this.marketing.unavailableMaterialIds,
				competitiveSamples: this.marketing.competitors.map(c => c.value),
				residentDoctorNames: this.marketing.residentDoctorNames.map(c => c.name).join(', '),
				payment,
				amountReceived,
				priceTypeId: this.priceTypeId
			}

			const now = this.serverTime ? moment(this.serverTime) : moment()
			const createdAt = moment(this.visitDate)
				.hour(now.hour())
				.minute(now.minute())
				.second(now.second())
				.format('YYYY-MM-DD[T]HH:mm:ss')

			const saveAction = this.visitId
				? this.$store.dispatch('visits/update', { id: this.visitId, ...visitPayload, createdAt })
				: this.$store.dispatch('visits/create', { ...visitPayload, createdAt })
			const savedMessageKey = this.visitId ? 'Visits.visit-is-updated' : 'Visits.visit-is-created'

			saveAction
				.then((visitResponse) => {
					const orderPayload = this.buildOrderPayload(visitResponse?.data)
					const hasOrder = useVisitOrderRequest && orderPayload.orderDetail.length

					if (visitResponse?.queued) {
						return (hasOrder
							? this.$store.dispatch('sync/attachOrder', { localId: visitResponse.localId, orderPayload })
							: Promise.resolve()
						).then(() => this.handleSaveSuccess('Visits.visit-is-queued'))
					}

					return (hasOrder
						? this.$store.dispatch('order/create', { order: orderPayload })
						: Promise.resolve()
					).then(() => this.handleSaveSuccess(savedMessageKey))
				})
				.finally(() => {
					this.loading = false
				})
		},

		back() {
			Promise.all([
				this.$store.dispatch('visitDraft/setPayments', this.payments),
				this.$store.dispatch('visitDraft/setDiscountAmount', this.discountAmount)
			]).then(() => {
				this.$router.push({ name: 'add-visit-details' })
			})
		}
	},

	created() {
		if (!this.entityId) {
			this.$router.replace({ name: 'add-visit-details' })
			return
		}

		this.payments = this.$store.state.visitDraft.payments
		this.discountAmount = this.$store.state.visitDraft.discountAmount
		this.ensureSingleRow()
	}
}
</script>

<style scoped>
.add-visit-payment {
	min-height: 100vh;
	background-color: #f2f7f6;
}

.add-visit-payment__header {
	cursor: pointer;
	background-color: #fff;
	border-bottom: 1px solid #e0e0e0;
}

.add-visit-payment__table thead th {
	background-color: transparent;
	border-bottom: none;
}

.add-visit-payment__table tbody td {
	border-right: 1px solid #e0e0e0;
	border-bottom: 1px solid #e0e0e0;
}

.add-visit-payment__row--empty {
	background-color: #f5f5f5;
}

.add-visit-payment__actions {
	gap: 12px;
}

.add-visit-payment__primary-btn,
.add-visit-payment__secondary-btn {
	flex: 1;
}

.add-visit-payment__footer {
	border-top: 1px solid #e0e0e0;
}
</style>
